import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { EnvelopeSimple, PaperPlaneTilt, X, User, Folder, UsersThree, Eye, Target } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { searchEmailRecipients, sendComposedEmail, listEmailSegments } from '../services/api';
import { PageHero } from '../components/motion/PageHero';

function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
}

export default function EmailComposePage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ contacts: [], cooperators: [], dossiers: [] });
  const [searching, setSearching] = useState(false);

  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedCoops, setSelectedCoops] = useState([]);
  const [selectedDossiers, setSelectedDossiers] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [segments, setSegments] = useState([]);
  const [extraEmails, setExtraEmails] = useState('');

  const [mode, setMode] = useState('html'); // 'html' | 'template'
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [templateId, setTemplateId] = useState('');
  const [paramsJson, setParamsJson] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [sending, setSending] = useState(false);

  const doSearch = useMemo(() => debounce(async (q) => {
    setSearching(true);
    try {
      const res = await searchEmailRecipients(q || '', 25);
      setResults(res.data);
    } catch {
      setResults({ contacts: [], cooperators: [], dossiers: [] });
    } finally {
      setSearching(false);
    }
  }, 250), []);

  useEffect(() => {
    doSearch(query);
  }, [query, doSearch]);

  useEffect(() => {
    listEmailSegments().then((r) => setSegments(r.data.items || [])).catch(() => setSegments([]));
  }, []);

  const toggle = (list, setList, item) => {
    if (list.some((x) => x.id === item.id)) {
      setList(list.filter((x) => x.id !== item.id));
    } else {
      setList([...list, item]);
    }
  };

  const totalRecipients = selectedContacts.length + selectedCoops.length + selectedDossiers.length + selectedSegments.length + (extraEmails.trim() ? extraEmails.split(',').filter((e) => e.trim()).length : 0);

  const handleSend = async () => {
    if (totalRecipients === 0) {
      toast.warning('Ajoutez au moins un destinataire');
      return;
    }
    if (mode === 'html' && (!subject.trim() || !html.trim())) {
      toast.warning('Sujet et contenu HTML requis');
      return;
    }
    if (mode === 'template' && !templateId) {
      toast.warning('ID de template Brevo requis');
      return;
    }
    let params = {};
    if (mode === 'template' && paramsJson.trim()) {
      try {
        params = JSON.parse(paramsJson);
      } catch {
        toast.error("JSON de paramètres invalide");
        return;
      }
    }
    const payload = {
      contact_ids: selectedContacts.map((x) => x.id),
      cooperator_ids: selectedCoops.map((x) => x.id),
      dossier_ids: selectedDossiers.map((x) => x.id),
      segment_ids: selectedSegments.map((x) => x.id),
      extra_emails: extraEmails.split(',').map((e) => e.trim()).filter(Boolean),
      reply_to: replyTo || undefined,
    };
    if (mode === 'template') {
      payload.template_id = Number(templateId);
      payload.params = params;
    } else {
      payload.subject = subject;
      payload.html = html;
    }
    setSending(true);
    try {
      const res = await sendComposedEmail(payload);
      toast.success(`Envoyé à ${res.data.recipients_count} destinataire(s) via ${res.data.provider}`);
      setSubject(''); setHtml(''); setTemplateId(''); setParamsJson('');
      setSelectedContacts([]); setSelectedCoops([]); setSelectedDossiers([]); setSelectedSegments([]); setExtraEmails('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="email-compose-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Composer un mail"
        subtitle="Éditeur avancé, personnalisation et envoi ciblé"
        icon={<EnvelopeSimple size={24} weight="duotone" className="text-white" />}
        gradient="from-[#D5007D] to-[#EA3C3A]"
        accents={['#FF8E00', '#00AEEF']}
      />

      <div>
        <h1 className="text-3xl font-bold text-[#1A2421] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Composer un mail
        </h1>
        <p className="text-[#5E6D66] mt-1">
          Envoyer un email transactionnel Brevo à un ou plusieurs contacts, coopérateurs ou dossiers.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Column 1: Recipient picker */}
        <Card className="border-[#E8E6E1] lg:col-span-1">
          <CardContent className="p-4 space-y-4">
            <div>
              <Label className="text-sm font-semibold">Destinataires</Label>
              <Input
                placeholder="Rechercher contact, coopérateur, dossier..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="mt-2 border-[#E8E6E1]"
                data-testid="email-compose-search"
              />
              {searching && <p className="text-xs text-[#5E6D66] mt-1">Recherche…</p>}
            </div>

            <Tabs defaultValue="contacts">
              <TabsList className="w-full">
                <TabsTrigger value="contacts" className="flex-1 text-xs" data-testid="tab-contacts">
                  <User size={14} className="mr-1" /> Contacts ({results.contacts.length})
                </TabsTrigger>
                <TabsTrigger value="coops" className="flex-1 text-xs" data-testid="tab-coops">
                  <UsersThree size={14} className="mr-1" /> Coop. ({results.cooperators.length})
                </TabsTrigger>
                <TabsTrigger value="dossiers" className="flex-1 text-xs" data-testid="tab-dossiers">
                  <Folder size={14} className="mr-1" /> Dossiers ({results.dossiers.length})
                </TabsTrigger>
                <TabsTrigger value="segments" className="flex-1 text-xs" data-testid="tab-segments">
                  <Target size={14} className="mr-1" /> Segments ({segments.length})
                </TabsTrigger>
              </TabsList>
              <TabsContent value="contacts" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
                {results.contacts.map((c) => {
                  const active = selectedContacts.some((x) => x.id === c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle(selectedContacts, setSelectedContacts, c)}
                      className={`w-full text-left p-2 rounded-md text-xs border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                      data-testid={`contact-pick-${c.id}`}
                    >
                      <div className="font-medium">{c.label}</div>
                      <div className={active ? 'text-white/80' : 'text-[#5E6D66]'}>{c.email}</div>
                    </button>
                  );
                })}
                {results.contacts.length === 0 && <p className="text-xs text-[#5E6D66] text-center py-2">Aucun contact</p>}
              </TabsContent>
              <TabsContent value="coops" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
                {results.cooperators.map((c) => {
                  const active = selectedCoops.some((x) => x.id === c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => toggle(selectedCoops, setSelectedCoops, c)}
                      className={`w-full text-left p-2 rounded-md text-xs border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                      data-testid={`coop-pick-${c.id}`}
                    >
                      <div className="font-medium">{c.label}</div>
                      <div className={active ? 'text-white/80' : 'text-[#5E6D66]'}>{c.email}</div>
                    </button>
                  );
                })}
                {results.cooperators.length === 0 && <p className="text-xs text-[#5E6D66] text-center py-2">Aucun coopérateur</p>}
              </TabsContent>
              <TabsContent value="dossiers" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
                {results.dossiers.map((d) => {
                  const active = selectedDossiers.some((x) => x.id === d.id);
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggle(selectedDossiers, setSelectedDossiers, d)}
                      className={`w-full text-left p-2 rounded-md text-xs border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                      data-testid={`dossier-pick-${d.id}`}
                    >
                      <div className="font-medium truncate">{d.label}</div>
                      <div className={active ? 'text-white/80' : 'text-[#5E6D66]'}>Contacts + coopérateurs du dossier</div>
                    </button>
                  );
                })}
                {results.dossiers.length === 0 && <p className="text-xs text-[#5E6D66] text-center py-2">Aucun dossier</p>}
              </TabsContent>
              <TabsContent value="segments" className="max-h-[300px] overflow-y-auto space-y-1 mt-2">
                {segments.map((seg) => {
                  const active = selectedSegments.some((x) => x.id === seg.id);
                  return (
                    <button
                      key={seg.id}
                      type="button"
                      onClick={() => toggle(selectedSegments, setSelectedSegments, seg)}
                      className={`w-full text-left p-2 rounded-md text-xs border ${active ? 'bg-[#2D5A4C] text-white border-[#2D5A4C]' : 'bg-white border-[#E8E6E1] hover:bg-[#F9F8F5]'}`}
                      data-testid={`segment-pick-${seg.id}`}
                    >
                      <div className="font-medium">{seg.name}</div>
                      <div className={active ? 'text-white/80' : 'text-[#5E6D66]'}>
                        {(seg.filters?.territoires || []).join(', ') || 'Tous territoires'}
                      </div>
                    </button>
                  );
                })}
                {segments.length === 0 && <p className="text-xs text-[#5E6D66] text-center py-2">Aucun segment. Créez-en dans "Segments ciblés".</p>}
              </TabsContent>
            </Tabs>

            <div>
              <Label className="text-xs">Emails additionnels (séparés par virgule)</Label>
              <Input
                placeholder="alice@x.fr, bob@y.fr"
                value={extraEmails}
                onChange={(e) => setExtraEmails(e.target.value)}
                className="mt-1 border-[#E8E6E1]"
                data-testid="email-compose-extra"
              />
            </div>
          </CardContent>
        </Card>

        {/* Column 2-3: Composer */}
        <Card className="border-[#E8E6E1] lg:col-span-2">
          <CardContent className="p-4 space-y-4">
            {/* Selected chips */}
            <div>
              <Label className="text-sm font-semibold flex items-center gap-2">
                <EnvelopeSimple size={16} />
                {totalRecipients} destinataire{totalRecipients > 1 ? 's' : ''}
              </Label>
              <div className="flex flex-wrap gap-1 mt-2 max-h-24 overflow-y-auto">
                {[...selectedContacts, ...selectedCoops].map((r) => (
                  <Badge key={r.id} variant="outline" className="text-[10px] flex items-center gap-1">
                    {r.label || r.email}
                    <button
                      onClick={() => {
                        setSelectedContacts((s) => s.filter((x) => x.id !== r.id));
                        setSelectedCoops((s) => s.filter((x) => x.id !== r.id));
                      }}
                      className="ml-1"
                    >
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
                {selectedDossiers.map((d) => (
                  <Badge key={d.id} className="bg-[#2D5A4C]/10 text-[#2D5A4C] text-[10px] flex items-center gap-1">
                    <Folder size={10} /> {d.label}
                    <button onClick={() => setSelectedDossiers((s) => s.filter((x) => x.id !== d.id))} className="ml-1">
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
                {selectedSegments.map((seg) => (
                  <Badge key={seg.id} className="bg-purple-100 text-purple-800 text-[10px] flex items-center gap-1">
                    <Target size={10} /> {seg.name}
                    <button onClick={() => setSelectedSegments((s) => s.filter((x) => x.id !== seg.id))} className="ml-1">
                      <X size={10} />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <Tabs value={mode} onValueChange={setMode}>
              <TabsList>
                <TabsTrigger value="html" data-testid="mode-html">Mail libre (HTML)</TabsTrigger>
                <TabsTrigger value="template" data-testid="mode-template">Template Brevo</TabsTrigger>
              </TabsList>
              <TabsContent value="html" className="space-y-3 mt-3">
                <div>
                  <Label className="text-sm">Sujet</Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 border-[#E8E6E1]" data-testid="email-compose-subject" />
                </div>
                <div>
                  <Label className="text-sm">Contenu (HTML)</Label>
                  <Textarea
                    value={html}
                    onChange={(e) => setHtml(e.target.value)}
                    rows={10}
                    placeholder="<p>Bonjour,</p><p>...</p>"
                    className="mt-1 border-[#E8E6E1] font-mono text-xs"
                    data-testid="email-compose-html"
                  />
                  <p className="text-[10px] text-[#5E6D66] mt-1">Le contenu sera enveloppé automatiquement dans la charte O'SCOP.</p>
                </div>
                <div className="flex items-center justify-between border-t border-[#E8E6E1] pt-3">
                  <Label className="text-xs flex items-center gap-1"><Eye size={12} /> Aperçu en direct</Label>
                  <Button size="sm" variant="outline" type="button" onClick={() => setShowPreview((s) => !s)} className="border-[#E8E6E1] text-xs" data-testid="toggle-preview-btn">
                    {showPreview ? 'Masquer' : 'Afficher'}
                  </Button>
                </div>
                {showPreview && (
                  <div className="border border-[#E8E6E1] rounded-md overflow-hidden" data-testid="html-preview">
                    <div className="bg-[#F9F8F5] px-3 py-2 text-[10px] text-[#5E6D66] flex items-center justify-between">
                      <span>Aperçu (charte O'SCOP)</span>
                      <span className="italic">Sujet : {subject || '(vide)'}</span>
                    </div>
                    <div className="p-4" style={{ fontFamily: 'Arial, sans-serif' }}>
                      <div style={{ maxWidth: 600, margin: 'auto' }}>
                        <div style={{ background: '#2D5A4C', color: '#fff', padding: '16px 20px', borderRadius: '8px 8px 0 0' }}>
                          <h2 style={{ margin: 0, fontSize: 18 }}>{subject || 'Communication O\'SCOP'}</h2>
                        </div>
                        <div
                          style={{ border: '1px solid #E8E6E1', borderTop: 0, padding: 20, borderRadius: '0 0 8px 8px', background: '#fff', minHeight: 100 }}
                          dangerouslySetInnerHTML={{ __html: html || '<p style="color:#8DB0A1;font-style:italic;">Écrivez votre contenu HTML à gauche pour voir l\'aperçu.</p>' }}
                        />
                        <p style={{ fontSize: 11, color: '#8DB0A1', textAlign: 'center', marginTop: 12 }}>
                          Objectif SCOP Outremer &mdash; CRM ESS
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="template" className="space-y-3 mt-3">
                <div>
                  <Label className="text-sm">ID Template Brevo</Label>
                  <Input
                    type="number"
                    value={templateId}
                    onChange={(e) => setTemplateId(e.target.value)}
                    placeholder="Ex: 1"
                    className="mt-1 border-[#E8E6E1]"
                    data-testid="email-compose-template-id"
                  />
                </div>
                <div>
                  <Label className="text-sm">Paramètres dynamiques (JSON)</Label>
                  <Textarea
                    value={paramsJson}
                    onChange={(e) => setParamsJson(e.target.value)}
                    rows={5}
                    placeholder='{"nom": "Dupont", "montant": "1500"}'
                    className="mt-1 border-[#E8E6E1] font-mono text-xs"
                    data-testid="email-compose-params"
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div>
              <Label className="text-sm">Reply-To (optionnel)</Label>
              <Input value={replyTo} onChange={(e) => setReplyTo(e.target.value)} placeholder="contact@..." className="mt-1 border-[#E8E6E1]" data-testid="email-compose-reply-to" />
            </div>

            <div className="flex justify-end pt-2 border-t border-[#E8E6E1]">
              <Button
                onClick={handleSend}
                disabled={sending || totalRecipients === 0}
                className="bg-[#2D5A4C] hover:bg-[#1E4136]"
                data-testid="email-compose-send-btn"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <PaperPlaneTilt size={16} className="mr-2" />}
                Envoyer
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
